// @ts-strict-ignore
import React from 'react'
import { RouteComponentProps } from 'react-router'
import { Helmet } from 'react-helmet'
import { IonRow } from '@ionic/react'
import { SettingsContainer as Container, Language, convertLanguages } from 'mytours-read-common'
import { getSettingsUrl } from '../../navigation/routes'
import SettingsService, { clearLocalStorage, getOriginalLanguageName } from '../functions'
import strings from '../../../strings'
import Page from '../../common/components/Page'
import Button from '../../common/components/Button'
import ConfirmationModal from '../../common/components/confirmation-modal'
import SettingsRow from './SettingsRow'
import './style.less'
import classNames from 'classnames'
import { getDirMode } from 'mobile_web/modules/platform'
import LanguageModal from 'mobile_web/modules/common/components/language-modal'
import db from 'mobile_web/modules/db'
import AudioPlayerBarComponent from 'mobile_web/modules/audio/components/AudioPlayerBar'
import { replaceLanguageCodeInPathname } from 'mobile_web/modules/navigation/utils'
import { ionHistory } from 'mobile_web/modules/navigation'
import { LanguageCode } from 'mytours-read-api-typescript'

interface State {
	showLanguagePopup: boolean
	languagesList: LanguagesList[]
	languageChanged: boolean
	clearingResultsDB?: boolean
	showConfirmationModal: boolean
}

interface LanguagesList {
	originalName: string
	name: string
	code: string
}

class Settings extends React.Component<Container.Props & Container.Actions & RouteComponentProps, State> {
	state: State = {
		showLanguagePopup: false,
		languagesList: [],
		languageChanged: false,
		clearingResultsDB: false,
		showConfirmationModal: false,
	}
	languageChangedTimeout = null

	componentDidMount(): void {
		this.setLanguageList()
		const refrer = (this.props.location?.state as any)?.prevUrlPath
		const isReplaceAction = this.props.history.action === 'REPLACE'

		if (isReplaceAction && refrer && refrer !== this.props.location.pathname) {
			this.setLanguageChanged()
		}
	}

	componentWillUnmount(): void {
		if (this.languageChangedTimeout !== null) clearTimeout(this.languageChangedTimeout)
	}

	setLanguageChanged = () => {
		this.setState({ languageChanged: true })
		this.languageChangedTimeout = setTimeout(() => {
			this.setState({ languageChanged: false })
		}, 2000)
	}

	setLanguageList = (): void => {
		const { appMetaData } = this.props

		let languages: Language[] = []
		if (appMetaData && appMetaData.supported_languages) {
			languages = convertLanguages(appMetaData.supported_languages)
		}

		const languagesList: LanguagesList[] = languages.map((language: Language) => {
			const originalName = getOriginalLanguageName(language.code)

			return {
				originalName,
				code: language.code,
				name: language.name || '',
			}
		})

		languagesList.sort((a: LanguagesList, b: LanguagesList) => {
			const textA = a.name.toUpperCase()
			const textB = b.name.toUpperCase()
			return textA < textB ? -1 : textA > textB ? 1 : 0
		})

		this.setState({ languagesList })
	}

	setShowLanguagePopup = (): void => {
		this.setState(prev => ({ showLanguagePopup: !prev.showLanguagePopup }))
	}

	onChooseLanguage = ({ code, name }: Language): void => {
		this.setShowLanguagePopup()

		ionHistory.replace(replaceLanguageCodeInPathname(this.props.appMetaData.supported_languages, code as LanguageCode))
		this.props.selectLanguage({ code, name })
		SettingsService.appLanguage.setCurrentLocale(code)
	}

	onUpdateMetaData = (): void => {
		clearLocalStorage()
		this.props.updateAppMetaData(this.props.appInfo.projectId)
		this.props.updateAppManifest()
	}

	handleClearQuizResults = async (): Promise<void> => {
		this.setState({ clearingResultsDB: true, showConfirmationModal: false })
		await db.quizResults.clear()
		setTimeout(() => {
			this.setState({ clearingResultsDB: false })
		}, 1000)
	}

	renderLanguagesRow = (): React.ReactNode => {
		const { selectedLanguage } = this.props

		const originalName = getOriginalLanguageName(selectedLanguage?.code)

		return (
			<SettingsRow
				labelId="languageLabel"
				label={`${strings.language}: `}
				value={
					<Button
						mode="ios"
						className="settings-value-button"
						onClick={this.setShowLanguagePopup}
						fill="clear"
						expand="block"
						ariaLabel={originalName}
						data-cy="settings-row-language-button"
					>
						{originalName || ''}
						<i className="fal fa-chevron-right chevron-right" />
					</Button>
				}
				data-cy="settings-row-language"
			/>
		)
	}

	render(): React.ReactElement {
		const { languageChanged, languagesList, clearingResultsDB, showConfirmationModal } = this.state
		const {
			loadingMetaData,
			selectedLanguage,
			appMetaData: { supported_languages: supportedLanguages },
			appManifest: { quizzes },
		} = this.props

		const hasQuizzes = quizzes?.length > 0

		return (
			<>
				<Helmet
					title={strings.settings}
					meta={[
						{ name: 'description', content: strings.settings },
						{ property: 'og:type', content: 'website' },
						{ property: 'og:title', content: strings.settings },
						{ property: 'og:description', content: strings.settings },
						{ property: 'og:url', content: window.location.href },
					]}
				/>
				<Page
					contentProps={{
						className: 'settings-content',
					}}
					headerProps={{
						title: strings.settings,
						hideBackButton: true,
						indentTitle: true,

						collectionItem: null,
						itemType: null,
						itemId: null,
						collection: null,
						screen: null,
						hideFavouriteButton: true
					}}
					footer={{
						component: <AudioPlayerBarComponent bottomOffset={-1} />,
					}}
					dataCy="page-settings"
				>
					<div className="block settings-block-list first-block">
						{languagesList.length > 1 && <div className="block-content">{this.renderLanguagesRow()}</div>}
					</div>

					<div className="block settings-block-buttons">
						<div className="block-content">
							<Button
								className={classNames('update-metadata-btn', { loading: loadingMetaData })}
								onClick={this.onUpdateMetaData}
								fill="clear"
								expand="block"
								ariaLabel={strings.update_metadata}
							>
								<IonRow className="update-button-inner" dir={getDirMode()}>
									<i className="load-icon fal fa-sync" />
									<span>{strings.update_metadata}</span>
								</IonRow>
							</Button>
							{hasQuizzes && (
								<Button
									className={classNames('update-metadata-btn', { loading: clearingResultsDB })}
									onClick={() => this.setState({ showConfirmationModal: true })}
									fill="clear"
									expand="block"
									ariaLabel={strings.clear_quiz_results}
								>
									<IonRow className="update-button-inner" dir={getDirMode()}>
										<i className={`load-icon fal fa-${clearingResultsDB ? 'sync' : 'circle-trash'}`} />
										<span>{strings.clear_quiz_results}</span>
									</IonRow>
								</Button>
							)}
						</div>
					</div>

					{languageChanged && (
						<div className="hidden" aria-live="assertive">
							{strings.formatString(strings.selected_language_loading, { name: selectedLanguage.name })}
						</div>
					)}

					{loadingMetaData && (
						<div className="hidden" aria-live="assertive">
							{strings.loading_meta_data}
						</div>
					)}

					<ConfirmationModal
						isOpen={showConfirmationModal}
						question={strings.clear_quiz_results_confirmation_text}
						onDidDismiss={() => this.setState({ showConfirmationModal: false })}
						onAccept={this.handleClearQuizResults}
					/>

					<LanguageModal
						selectedLanguage={selectedLanguage}
						isOpen={this.state.showLanguagePopup}
						closeModal={this.setShowLanguagePopup}
						supportedLanguages={supportedLanguages}
						selectLanguage={lang => this.onChooseLanguage(lang)}
						onDidDismiss={() => this.setState({ showLanguagePopup: false })}
					/>
				</Page>
			</>
		)
	}
}

const SettingsComponent = Container.default(Settings)
export default SettingsComponent
